import { createRoute, redirect } from '@tanstack/react-router';
import { z } from 'zod';
import { setWorkspaceSlug } from '../../../lib/workspace';
import { loadSession, publicLayoutRoute } from '../../../router/parents';
import { WithCompany } from './branded';
import { CheckEmailPage, ForgotPasswordPage, ResetPasswordPage, SignInPage } from './pages';

/**
 * Company-branded sign-in on the single host: `/c/<slug>` (alias `/c/<slug>/sign-in`), plus the
 * branded forgot / reset password and "check your email" pages. Mounted under the public layout
 * by router/index.tsx. A signed-in visitor goes straight into that company's workspace.
 */

function safeTarget(target: string | undefined): string {
  return target && target.startsWith('/') && !target.startsWith('//') ? target : '/';
}

const signInSearch = z.object({ redirect: z.string().max(2048).optional().catch(undefined) });

async function enterIfSignedIn(queryClient: Parameters<typeof loadSession>[0], slug: string, target: string | undefined) {
  if (await loadSession(queryClient)) {
    setWorkspaceSlug(slug.toLowerCase());
    throw redirect({ to: safeTarget(target) });
  }
}

const companyRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: '/c/$slug',
  validateSearch: signInSearch,
  beforeLoad: ({ context, params, search }) => enterIfSignedIn(context.queryClient, params.slug, search.redirect),
  component: function CompanySignInRoute() {
    const { slug } = companyRoute.useParams();
    const { redirect: target } = companyRoute.useSearch();
    return <WithCompany slug={slug}>{(company) => <SignInPage redirect={target} company={company} />}</WithCompany>;
  },
});

const companySignInRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: '/c/$slug/sign-in',
  validateSearch: signInSearch,
  beforeLoad: ({ context, params, search }) => enterIfSignedIn(context.queryClient, params.slug, search.redirect),
  component: function CompanySignInAliasRoute() {
    const { slug } = companySignInRoute.useParams();
    const { redirect: target } = companySignInRoute.useSearch();
    return <WithCompany slug={slug}>{(company) => <SignInPage redirect={target} company={company} />}</WithCompany>;
  },
});

const companyCheckEmailRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: '/c/$slug/check-email',
  validateSearch: z.object({ email: z.string().max(320).optional().catch(undefined) }),
  component: function CompanyCheckEmailRoute() {
    const { slug } = companyCheckEmailRoute.useParams();
    const { email } = companyCheckEmailRoute.useSearch();
    return <WithCompany slug={slug}>{(company) => <CheckEmailPage email={email} company={company} />}</WithCompany>;
  },
});

const companyForgotRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: '/c/$slug/forgot-password',
  component: function CompanyForgotRoute() {
    const { slug } = companyForgotRoute.useParams();
    return <WithCompany slug={slug}>{(company) => <ForgotPasswordPage company={company} />}</WithCompany>;
  },
});

const companyResetRoute = createRoute({
  getParentRoute: () => publicLayoutRoute,
  path: '/c/$slug/reset-password',
  validateSearch: z.object({ token: z.string().max(512).optional().catch(undefined) }),
  component: function CompanyResetRoute() {
    const { slug } = companyResetRoute.useParams();
    const { token } = companyResetRoute.useSearch();
    return <WithCompany slug={slug}>{(company) => <ResetPasswordPage token={token} company={company} />}</WithCompany>;
  },
});

export const companySignInRoutes = [companyRoute, companySignInRoute, companyCheckEmailRoute, companyForgotRoute, companyResetRoute] as const;
